import React, { useRef, useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import './ReportPreview.css';
import { supabase } from '../supabaseClient';
import RadarChart from '../components/RadarChart';
import { calculateModuleAverages } from '../moduleAverages';

// Только для отображения читаемых названий тестов на карточках —
// не методология, просто локализация ID теста в заголовок.
const TEST_DASHBOARD_NAMES = {
  SoundTest: 'Слуховое восприятие',
  MusicPerception: 'Восприятие музыки',
  RhythmTest: 'Восприятие ритма',
  PhonemicPerception: 'Фонематическая дифференциация',
  SoundInWord: 'Звук в слове',
  SoundPosition: 'Позиция звука в слове',
  VisionTest: 'Восприятие цвета и формы',
  SizeTest: 'Восприятие величины',
  VisionPerception: 'Восприятие в условиях наложений',
  SpatialDirections: 'Ориентирование в пространстве',
  BodyScheme: 'Ориентировка в схеме тела',
  Puzzle: 'Складывание картинок из частей',
  SeguinBoard: 'Доска Сегена',
  Mentalrotation: 'Мысленное вращение',
  VocabularyTest: 'Пассивный словарь',
  AnimalBabies: 'Детёныши животных',
  WordFormation: 'Множественное число сущ.',
  WordFormationDiminutive: 'Уменьшительно-ласкательные суфф.',
  VerbsFormation: 'Формы глаголов',
  SentenceComprehension: 'Понимание предложений',
  StorySequence: 'Понимание связной речи',
  NegationsTest: 'Отрицания и исключения',
  MultiStepInstr: 'Многоступенчатые инструкции',
  Antonyms: 'Подбор антонимов',
  Generalization: 'Обобщение',
  Verbs: 'Глагольная лексика',
};

const ReportPreview = ({
  profile,
  test_scores,
  expectedTests = [],
  test_details = {},
  onClose,
  isHistoryView = false,
  // НОВОЕ: при просмотре из истории DashboardPage передаёт уже готовые
  // данные напрямую из строки test_reports — отчёт не пересчитывается,
  // это тот же снимок, что человек видел в момент прохождения.
  descriptionBlocks: initialDescriptionBlocks = null,
  testGrades: initialTestGrades = null,
}) => {
  const reportRef = useRef(null);
  const [conclusion, setConclusion] = useState(
    'Результаты тестирования показывают наличие речевых особенностей, требующих коррекционной работы. '
  );

  const historyDataReady = isHistoryView && initialDescriptionBlocks !== null && initialTestGrades !== null;

  const [descriptionBlocks, setDescriptionBlocks] = useState(initialDescriptionBlocks || []);
  const [testGrades, setTestGrades] = useState(initialTestGrades || {});
  const [isLoading, setIsLoading] = useState(!historyDataReady);
  const [loadError, setLoadError] = useState(null);
  const hasRequested = useRef(false);

  // ====================================================================
  // ГЕНЕРАЦИЯ ОТЧЁТА — только для нового прохождения теста. Если это
  // просмотр из истории и данные уже переданы пропсами — сервер вообще
  // не вызывается.
  // ====================================================================
  useEffect(() => {
    if (historyDataReady) return;
    if (hasRequested.current) return;
    if (!test_scores || Object.keys(test_scores).length === 0) return;

    hasRequested.current = true;
    setIsLoading(true);
    setLoadError(null);

    const fetchReport = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('generate-report', {
          body: { test_scores, test_details, expectedTests, isHistoryView },
        });

        if (error) throw error;

        setDescriptionBlocks(data?.descriptionBlocks || []);
        setTestGrades(data?.testGrades || {});
      } catch (err) {
        console.error('Ошибка при генерации отчёта:', err);
        // ВРЕМЕННО для диагностики: supabase-js прячет тело ответа сервера
        // внутри err.context (Response) — печатаем его явно, чтобы увидеть
        // настоящую причину ошибки (обычный console.error(err) её не показывает).
        if (err?.context) {
          try {
            const bodyText = await err.context.text();
            console.error('[ДИАГНОСТИКА] Статус:', err.context.status, 'Тело ответа:', bodyText);
          } catch (readErr) {
            console.error('[ДИАГНОСТИКА] Не удалось прочитать тело ответа:', readErr);
          }
        }
        setLoadError('Не удалось сформировать отчёт. Попробуйте обновить страницу.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [historyDataReady, test_scores, test_details, expectedTests, isHistoryView]);

  const handleDownloadPdf = () => {
    const element = reportRef.current;
    const opt = {
      margin: 10,
      filename: `Report_${profile?.name || 'child'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };
    html2pdf().set(opt).from(element).save();
  };

  const testIds = expectedTests.length > 0 ? expectedTests : Object.keys(test_scores || {});
  const testsToRender = testIds.map((id) => {
    const g = testGrades[id] || {};
    return {
      id,
      name: TEST_DASHBOARD_NAMES[id] || id,
      score: g.score,
      lvl: { color: g.color || '#6c757d', label: g.label || '—' },
    };
  });

  if (isLoading) {
    return (
      <div className="report-overlay">
        <div className="report-modal">
          <div className="report-toolbar">
            <button onClick={onClose} className="btn-close">Закрыть</button>
          </div>
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            Формируем отчёт…
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="report-overlay">
        <div className="report-modal">
          <div className="report-toolbar">
            <button onClick={onClose} className="btn-close">Закрыть</button>
          </div>
          <div style={{ padding: '60px', textAlign: 'center', color: '#dc3545' }}>
            {loadError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-overlay">
      <div className="report-modal">
        <div className="report-toolbar">
          <button onClick={onClose} className="btn-close">Закрыть</button>
          <button onClick={handleDownloadPdf} className="btn-download">📥 Скачать в PDF</button>
        </div>

        <div ref={reportRef} className="report-document">
          <h1 style={{ textAlign: 'center', color: '#1E3A8A', marginBottom: '20px' }}>Общий профиль развития</h1>

          <div className="profile-box">
            <p><strong>ФИО ребенка:</strong> {profile?.name}</p>
            <p><strong>Возраст:</strong> {profile?.age}</p>
            <p><strong>Анамнез:</strong> {profile?.anamnesis}</p>
          </div>

          <div className="radar-section">
            <RadarChart modulesData={calculateModuleAverages(test_scores)} />
            <p style={{ textAlign: 'center', fontSize: '14px', color: '#64748b', marginTop: '15px', marginBottom: '30px' }}>
              График отображает усредненный процент успешности по каждому из направлений оценки.
            </p>
          </div>

          {descriptionBlocks.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '5px' }}>Пройденные разделы обследования</h2>
              <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                {descriptionBlocks.map((block, i) => (
                  <li key={i}>{block.header}</li>
                ))}
              </ul>
            </div>
          )}

          <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '5px' }}>Результаты тестирования</h2>

          <div className="dashboard-grid">
            {testsToRender.map(({ id, name, score, lvl }) => (
              <div key={id} className="dashboard-card">
                <div className="card-header">
                  <span className="test-name">{name}</span>
                  <span className="test-score" style={{ background: lvl.color }}>
                    {score !== undefined ? `${score}%` : 'Н/Д'}
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar-fill" style={{ width: `${score || 0}%`, background: lvl.color }}></div>
                </div>
                <div className="card-footer" style={{ color: lvl.color }}>{lvl.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '30px' }}>
            {descriptionBlocks.map((block, index) => (
              <div key={index} style={{ marginBottom: '25px' }}>
                <h2 style={{ color: '#333', marginBottom: '15px' }}>{block.header}</h2>

                {block.subsections.map((sub, subIndex) => (
                  <div key={subIndex} style={{ marginBottom: '18px' }}>
                    {sub.title && (
                      <h3 style={{ color: '#444', marginBottom: '8px', fontSize: '16px' }}>{sub.title}</h3>
                    )}

                    {sub.paragraphs.map((text, i) => (
                      <p key={i} style={{ lineHeight: '1.6', textAlign: 'justify', margin: '4px 0' }}>{text}</p>
                    ))}

                    {sub.categoryCards && (
                      <div className="dashboard-grid-mini" style={{ marginTop: '10px' }}>
                        {sub.categoryCards.map(({ id, name, score, color }) => (
                          <div key={id} className="dashboard-card-mini">
                            <div className="card-header-mini">
                              <span className="test-name-mini">{name}</span>
                              <span className="test-score-mini" style={{ background: color }}>{score}%</span>
                            </div>
                            <div className="progress-bar-container">
                              <div className="progress-bar-fill" style={{ width: `${score}%`, background: color }}></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <h2 style={{ borderBottom: '2px solid #eee', paddingBottom: '5px', marginTop: '30px' }}>Логопедическое заключение</h2>

          <div
            className="editable-textarea"
            contentEditable={true}
            suppressContentEditableWarning={true}
            onBlur={(e) => setConclusion(e.target.innerText)}
          >
            {conclusion}
          </div>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
            * Нажмите на текст выше, чтобы отредактировать заключение перед скачиванием.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;